import { SELECTION_KEY } from '@techchange/pm-specs';
import { Plugin, PluginKey } from 'prosemirror-state';

import getSelectedLink from '../../utils/getSelectedLink';

export const linkSelectionPluginKey = new PluginKey('linkInputPlugin');

/**
 * Plugin that manages the state for link selection.
 *
 * @returns {Object} Prosemirror Plugin object.
 */
const linkSelectionPlugin = () =>

	new Plugin({

		key: linkSelectionPluginKey,

		state: {

			// Initializes the plugin state
			init() {

				return {

					/**
				 	 * The currently selected LinkMark.
				 	 */
					selectedLink: null,

					/**
				 	 * The start position of the LinkMark.
				 	 */
					start: null,

					/**
				 	 * The end position of the LinkMark.
				 	 */
					end: null,

					/**
					 * Flag specifying if the link tooltip should be shown.
					 */
					showTooltip: false,

				};

			},

			// Updates the plugin state given incoming editor transaction
			apply(tr, pluginState) {

				const toggleTooltip = tr.getMeta('toggleLinkTooltip');
				const selectedLink = tr.getMeta('selectedLink');
				const start = tr.getMeta('linkStart');
				const end = tr.getMeta('linkEnd');

				const newState = {
					selectedLink: (selectedLink === undefined) ? pluginState.selectedLink : selectedLink,
					start: (start === undefined) ? pluginState.start : start,
					end: (end === undefined) ? pluginState.end : end,
					showTooltip: toggleTooltip ? !pluginState.showTooltip : pluginState.showTooltip,
				};

				return newState;

			}

		},

		props: {

			// Checks if user moved cursor with keyboard and shows or hides tooltip accordingly
			handleKeyDown(view, event) {

				const { tr, schema, selection: { from, to } } = view.state;

				// Get current plugin state
				const pluginState = linkSelectionPluginKey.getState(view.state);
				const { selectedLink, showTooltip } = pluginState;

				const keyTypes = [
					'ArrowDown',
					'ArrowUp',
					'ArrowLeft',
					'ArrowRight',
				];

				// Check if user is navigating text with arrow keys
				if (!keyTypes.includes(event.key)) {
					return;
				}

				// Don't check for link if user is selecting text
				if (from !== to) {
					return;
				}

				const { linkMark, start, end } = getSelectedLink(view.state, from);

				if (linkMark) {

					tr.setMeta('selectedLink', linkMark);
					tr.setMeta('linkStart', start);
					tr.setMeta('linkEnd', end);

					// Show the tooltip if needed
					if (!showTooltip) {

						tr.setMeta('toggleLinkTooltip', true);

					}

				} else {

					// Hide the tooltip if needed
					if (showTooltip) {

						tr.setMeta('toggleLinkTooltip', true);
						tr.removeMark(0, tr.doc.content.size, schema.mark(SELECTION_KEY, { visible: true }));

					}

					// De-select link if needed
					if (selectedLink) {

						tr.setMeta('selectedLink', null);
						tr.setMeta('linkStart', null);
						tr.setMeta('linkEnd', null);

					}

				}

				// Only dispatch if we've set at least one meta value
				if (!tr.isGeneric) {

					view.dispatch(tr);

				}

			},

			// Checks if user clicked on a link and shows or hides tooltip accordingly
			handleClick(view, mousePos) {

				const { tr, schema } = view.state;

				// Get current plugin state
				const pluginState = linkSelectionPluginKey.getState(view.state);
				const { selectedLink, showTooltip } = pluginState;

				const { linkMark, start, end } = getSelectedLink(view.state, mousePos);

				if (linkMark) {

					tr.setMeta('selectedLink', linkMark);
					tr.setMeta('linkStart', start);
					tr.setMeta('linkEnd', end);

					// Show the tooltip if needed
					if (!showTooltip) {

						tr.setMeta('toggleLinkTooltip', true);

					}

				} else {

					// Hide the tooltip if needed
					if (showTooltip) {

						tr.setMeta('toggleLinkTooltip', true);
						tr.removeMark(0, tr.doc.content.size, schema.mark(SELECTION_KEY, { visible: true }));

					}

					// De-select link if needed
					if (selectedLink) {

						tr.setMeta('selectedLink', null);
						tr.setMeta('linkStart', null);
						tr.setMeta('linkEnd', null);

					}

				}

				// Only dispatch if we've set at least one meta value
				if (!tr.isGeneric) {

					view.dispatch(tr);

				}

			},

		},

	});

export default linkSelectionPlugin;
